/**
 * Portfolio / dashboard data layer (ARCHITECTURE.md §7, §8).
 *
 * Every read funnels through the typed `hc<AppType>` client and {@link unwrap}
 * (or {@link unwrapOrNull}), so failures surface as `ApiError` and TanStack
 * Query owns all server state. Query keys come from the shared `lib/queryKeys`
 * registry so a mutation can invalidate exactly what it touched. The portfolio
 * read endpoints answer an empty-but-200 body when the instance has no
 * account/snapshot yet (§7), so the charts render honest empty states rather
 * than errors on a fresh install; the two endpoints that *can* 404 (`summary`)
 * or is feature-gated (`holdings`) go through `unwrapOrNull` so the UI can
 * branch without a try/catch at the call site.
 */
import { queryOptions } from "@tanstack/react-query";
import { CurrencySchema, DecimalSchema, PortfolioPeriodSchema } from "@richrich/contracts";
import type {
  Currency,
  ListCashflowsResponse,
  ListStrategiesResponse,
  PortfolioAllocationResponse,
  PortfolioDecompositionResponse,
  PortfolioHoldingsResponse,
  PortfolioPeriod,
  PortfolioSummaryResponse,
  PortfolioTimeseriesResponse,
  SnapshotNowResponse,
  StrategyPnlResponse,
} from "@richrich/contracts";
import { api, unwrap, unwrapOrNull } from "../../lib/api";
import { keys } from "../../lib/queryKeys";
import { accountsQuery } from "../settings/api";

/** Report base currency fallback before any snapshot/settings load (§2). */
export const DEFAULT_BASE_CURRENCY: Currency = CurrencySchema.parse("KRW");

/** Default portfolio window (branded) when `?period` is absent. */
export const DEFAULT_PERIOD: PortfolioPeriod = PortfolioPeriodSchema.parse("90d");

/**
 * Parse the `?period` search param, tolerating garbage: an unrecognized value
 * yields `undefined` so the field stays optional in typed navigation (a required
 * search param would force every `navigate({ to: '/' })` to carry it).
 */
export function parsePeriodSearch(raw: unknown): { period?: PortfolioPeriod } {
  const parsed = PortfolioPeriodSchema.safeParse(raw);
  return parsed.success ? { period: parsed.data } : {};
}

const MS_PER_DAY = 86_400_000;

/** Lower-bound span (ms) per period; `all` has no lower bound (from = 0). */
const PERIOD_SPAN_MS: Record<PortfolioPeriod, number | null> = {
  "1d": MS_PER_DAY,
  "7d": 7 * MS_PER_DAY,
  "30d": 30 * MS_PER_DAY,
  "90d": 90 * MS_PER_DAY,
  "1y": 365 * MS_PER_DAY,
  all: null,
};

/**
 * Resolve a `{from,to}` epoch-ms window for the decomposition endpoint from a
 * period token. `all` spans from 0 so every snapshot is in range. `to` is
 * quantized to the start of the current minute so the derived query key is stable
 * across a render pass (and matches a loader prefetch computed a few ms earlier)
 * instead of changing on every millisecond.
 */
export function periodToRange(
  period: PortfolioPeriod,
  now: number = Date.now(),
): { from: number; to: number } {
  const to = Math.floor(now / 60_000) * 60_000;
  const span = PERIOD_SPAN_MS[period];
  return { from: span === null ? 0 : to - span, to };
}

/** Canonical zero decimal for empty-state fallbacks (branded, no float). */
const ZERO_DECIMAL = DecimalSchema.parse("0");

/** An empty timeseries payload so the chart can render a `loading`/empty frame. */
export function emptyTimeseries(
  period: PortfolioPeriod,
  baseCurrency: Currency = DEFAULT_BASE_CURRENCY,
): PortfolioTimeseriesResponse {
  return { period, baseCurrency, points: [] };
}

/** An empty decomposition payload (zeroed) for the loading/empty frame. */
export function emptyDecomposition(
  from: number,
  to: number,
  baseCurrency: Currency = DEFAULT_BASE_CURRENCY,
): PortfolioDecompositionResponse {
  return {
    from,
    to,
    baseCurrency,
    startValue: ZERO_DECIMAL,
    endValue: ZERO_DECIMAL,
    externalFlow: ZERO_DECIMAL,
    marketPnl: ZERO_DECIMAL,
    twr: 0,
    points: [],
  };
}

/** An empty allocation payload (zeroed) for the loading/empty frame. */
export function emptyAllocation(
  baseCurrency: Currency = DEFAULT_BASE_CURRENCY,
): PortfolioAllocationResponse {
  return { baseCurrency, stocksValue: ZERO_DECIMAL, cashValue: ZERO_DECIMAL, bySymbol: [] };
}

/** `GET /api/portfolio/summary` — latest snapshot, or `null` when none exists. */
export const summaryQuery = queryOptions({
  queryKey: keys.portfolio.summary(),
  queryFn: async (): Promise<PortfolioSummaryResponse | null> =>
    // No account / no snapshot yet → a legitimate empty state, not an error.
    unwrapOrNull<PortfolioSummaryResponse>(await api.portfolio.summary.$get({ query: {} })),
  staleTime: 30_000,
});

/** `GET /api/portfolio/timeseries?period` — total-value series over the window. */
export function timeseriesQuery(period: PortfolioPeriod) {
  return queryOptions({
    queryKey: keys.portfolio.timeseries(period),
    queryFn: async (): Promise<PortfolioTimeseriesResponse> =>
      unwrap<PortfolioTimeseriesResponse>(
        await api.portfolio.timeseries.$get({ query: { period } }),
      ),
    staleTime: 30_000,
  });
}

/** `GET /api/portfolio/decomposition?from&to` — market-P&L vs external-flow split. */
export function decompositionQuery(from: number, to: number) {
  return queryOptions({
    queryKey: keys.portfolio.decomposition(from, to),
    queryFn: async (): Promise<PortfolioDecompositionResponse> =>
      unwrap<PortfolioDecompositionResponse>(
        await api.portfolio.decomposition.$get({
          query: { from: String(from), to: String(to) },
        }),
      ),
    staleTime: 30_000,
  });
}

/** `GET /api/portfolio/allocation` — stocks/cash split + per-symbol weights. */
export const allocationQuery = queryOptions({
  queryKey: keys.portfolio.allocation(),
  queryFn: async (): Promise<PortfolioAllocationResponse> =>
    unwrap<PortfolioAllocationResponse>(await api.portfolio.allocation.$get({ query: {} })),
  staleTime: 30_000,
});

/**
 * `GET /api/portfolio/holdings` — live Toss positions (§7, `trading`-gated). Only
 * enable this when the `toss` feature is unlocked; NO_ACCOUNT (404) resolves to
 * `null` so a connected-but-empty account renders an empty table.
 */
export const holdingsQuery = queryOptions({
  queryKey: keys.portfolio.holdings(),
  queryFn: async (): Promise<PortfolioHoldingsResponse | null> =>
    unwrapOrNull<PortfolioHoldingsResponse>(await api.portfolio.holdings.$get({ query: {} })),
  staleTime: 15_000,
});

/** `POST /api/portfolio/snapshot` — operator-triggered on-demand snapshot for every account. */
export async function triggerSnapshot(): Promise<SnapshotNowResponse> {
  return unwrap<SnapshotNowResponse>(await api.portfolio.snapshot.$post());
}

/** `GET /api/portfolio/cashflows` — the deposit/withdrawal/dividend/fee ledger. */
export const cashflowsQuery = queryOptions({
  queryKey: keys.portfolio.cashflows(),
  queryFn: async (): Promise<ListCashflowsResponse> =>
    unwrap<ListCashflowsResponse>(await api.portfolio.cashflows.$get({ query: {} })),
  staleTime: 30_000,
});

/**
 * `GET /api/accounts` — cached broker accounts. Settings owns this domain (§4.1
 * dedup — it also owns the sync mutation); re-exported here so route/component
 * imports don't have to reach across features for it.
 */
export { accountsQuery };

/**
 * `GET /api/strategies` — the strategy roster (P&L is fetched per-row).
 * Shares `keys.strategies.list()` with the other unfiltered reads (chat's
 * refine selector, the strategies feature) — see queryKeys.ts.
 */
export const strategiesQuery = queryOptions({
  queryKey: keys.strategies.list(),
  queryFn: async (): Promise<ListStrategiesResponse> =>
    unwrap<ListStrategiesResponse>(await api.strategies.$get({ query: {} })),
  staleTime: 30_000,
});

/** `GET /api/strategies/:id/pnl` — one strategy's realized-P&L / win-rate snapshot. */
export function strategyPnlQuery(id: string) {
  return queryOptions({
    queryKey: keys.strategies.pnl(id),
    queryFn: async (): Promise<StrategyPnlResponse> =>
      unwrap<StrategyPnlResponse>(await api.strategies[":id"].pnl.$get({ param: { id } })),
    staleTime: 30_000,
  });
}
